import type { BillingEntityOwnedOrganization } from '@reflex/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';

/**
 * Sentinel for "auto-join off". Radix Select cannot hold `null` and rejects
 * an empty-string item value, so the off state needs a value of its own.
 * Safe against collision: organization ids are `coreIds` values (`org_…`),
 * never a bare word.
 */
const AUTO_JOIN_OFF = '__auto_join_off__';

export interface AutoJoinDestinationSelectProps {
  /** Current destination, or null when auto-join is off. */
  value: string | null;
  /** Candidate destinations: the organizations the billing entity owns. */
  organizations: BillingEntityOwnedOrganization[];
  disabled?: boolean;
  /** Accessible name; both surfaces phrase it with the claim's domain. */
  ariaLabel: string;
  id?: string;
  className?: string;
  'data-testid'?: string;
  onChange: (organizationId: string | null) => void;
}

/**
 * The picker for a domain's single auto-join destination, shared by the
 * org-admin and platform-admin claim surfaces so the sentinel, the option
 * rendering, and the null round-trip have exactly one definition.
 */
export function AutoJoinDestinationSelect({
  value,
  organizations,
  disabled,
  ariaLabel,
  id,
  className,
  'data-testid': dataTestId,
  onChange,
}: AutoJoinDestinationSelectProps) {
  return (
    <Select
      value={value ?? AUTO_JOIN_OFF}
      disabled={disabled}
      onValueChange={(next) => onChange(next === AUTO_JOIN_OFF ? null : next)}
    >
      <SelectTrigger id={id} className={className} aria-label={ariaLabel} data-testid={dataTestId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO_JOIN_OFF}>Off</SelectItem>
        {organizations.map((org) => (
          <SelectItem key={org.id} value={org.id}>
            {org.name} ({org.slug})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
